import axios from 'axios';
import {AssistantVariant, ConversationResponse, DocumentProps, Message, PdfDocument} from '../models/models';
import {extractCitations, extractThinking, mapServerMessageToClientMessage,} from '../utils/chat-utils';
import {
    ConversationDetailsResponse,
    ConversationDocumentInfo,
    LastAnswerVariantInfo
} from "../models/conversation.models";

// TODO: verify models from BE
export interface ConversationData {
    id: number;
    title: string;
    messages: Message[];
    answerVariants: AssistantVariant[];
    selectedGroupIds: number[];
    selectedDocIds: number[];
    selectedWholeCollectionIds: number[];
    selectedModelId: number | null;
    searchInternet: boolean;
    documents: ConversationDocumentInfo[];
}

export interface ConversationDetailResponse {
    id: number;
    title: string;
    messages?: Message[];
    conversationCollections?: Array<{
        id: number;
        name: string;
        lastModificationDate: string;
        collectionType: string;
        amountOfDocuments: number;
        owner: any;
    }>;
    documentIds?: number[];
    lastAnswerVariants?: AssistantVariant[];
    selectedModelId?: number | null;
    searchInternet?: boolean;
    documents?: ConversationDocumentInfo[];
}

export interface CreateConversationRequestBody {
    'collectionIds': Array<number>,
    'documentIds': Array<number>,
}

// TODO : fix conversationId type to number
export class ConversationService {
    async createConversation(requestBody: CreateConversationRequestBody, isLocalDocumentCollection: boolean): Promise<string> {
        const response = await axios.post<{ id: string }>(
            '/chat/conversation',
            requestBody,
            {
                params: { isLocalDocumentCollection }
            });
        return String(response.data.id);
    }

    async fetchConversation(id: string): Promise<ConversationData> {
        const response = await axios.get<ConversationDetailsResponse>(`/chat/conversation/${id}`);

        const data = response.data;

        const mappedMessages = (data.messages || []).map(msg => mapServerMessageToClientMessage(msg, data.documents ?? []));

        return {
            id: parseInt(id, 10),
            title: '',
            messages: mappedMessages,
            answerVariants: (data.lastAnswerVariants || []).map((v: LastAnswerVariantInfo) => {
                const {thinkingText, cleanText: textWithoutThinking} = extractThinking(v.content);
                const {cleanText, citations} = extractCitations(textWithoutThinking, data.documents ?? []);

                return {
                    id: v.id,
                    timestamp: v.timestamp,
                    content: cleanText,
                    thinking: thinkingText,
                    active: v.active,
                    documents: v.documents || [],
                    citations: citations.length > 0 ? citations : undefined,
                };
            }),
            selectedGroupIds: (data.conversationCollections || []).map(c => c.id),
            selectedDocIds: (data.documents || []).map(d => d.id),
            selectedWholeCollectionIds: [],
            selectedModelId: data.selectedModelId || null,
            searchInternet: false,
            documents: data.documents || [],
        };
    }

    async updateCollections(conversationId: string, selectedGroupIds: number[]): Promise<void> {
        await axios.patch(
            `/chat/conversation/${conversationId}/collections`,
            selectedGroupIds
        );
    }

    async updateDocuments(conversationId: string, selectedDocIds: number[]): Promise<void> {
        await axios.patch(`/chat/conversation/${conversationId}/documents`, {
            documentIds: selectedDocIds,
        });
    }

    async activateVariant(variantId: number): Promise<void> {
        await axios.patch(`/chat/answer/${variantId}/activate`);
    }

    async exportToPdf(conversationId: string): Promise<Blob> {
        const response = await axios.get(`/chat/conversation/${conversationId}/export`, {
            responseType: 'blob',
        });
        return response.data;
    }

    async cancelGeneration(conversationId: string): Promise<void> {
        await axios.post(`/chat/cancel/${conversationId}`).catch(err => {
            if (err.response?.status === 404) return;
            throw err;
        });
    }

    async fetchConversationsHistory(): Promise<ConversationResponse[]> {
        const response = await axios.get('/chat/conversation/history');
        return response.data;
    }

    async updateConversationTitle(conversationId: string, title: string): Promise<void> {
        await axios.patch(`/chat/conversation/${conversationId}/title`, null, {
            params: { title }
        });
    }

    async pinConversation(conversationId: string): Promise<void> {
        await axios.patch(`/chat/conversation/${conversationId}/pin`);
    }

    async unpinConversation(conversationId: string): Promise<void> {
        await axios.patch(`/chat/conversation/${conversationId}/unpin`);
    }

    async deleteConversation(conversationId: string): Promise<void> {
        await axios.delete(`/chat/conversation/${conversationId}`);
    }

    // TODO: verify endpoint response data type
    async fetchConversationCollections(conversationId: string, page?: number, size?: number): Promise<any> {
        if (!page) page = 0;
        if (!size) size = 10;

        const response = await axios.get(
            `/chat/conversation/${conversationId}/collections`,
            {
                params: { page, size }
            });
        return response.data;
    }

    async fetchConversationDocumentsInfo(conversationId: string): Promise<DocumentProps[]> {
        const response = await axios.get(`/chat/conversation/${conversationId}/document-info`);
        return response.data;
    }
}

export const conversationService = new ConversationService();
